import { AppLayout, Flashbar, FlashbarProps, Link, SplitPanel, Tabs } from '@amzn/awsui-components-core';
import React, { useState } from 'react';
import { useFinTechContext } from 'src/components/context/FintechContextProvider';

import { buildBreadcrumbChain } from 'src/utilities/RouteConfig';
import { DFPHomePageSideNavigation } from '../../dfp-home-page/DFPHomePageSideNavigation';
import { ChartSummaryContext } from '../../generic-components/ai-summary-components/ChartSummaryContext';
import { ChartSummaryPanel } from '../../generic-components/ai-summary-components/ChartSummaryPanel';
import { StatusType } from '../sas/SasInterfaces';
import { DogmaContext } from './DogmaContext';
import { DogmaDetailsPage } from './DogmaDetailsTab';
import { DogmaOverviewPage } from './DogmaOverview';
import { DogmaSelectionFilters } from './DogmaSelectionFilters';

export const DogmaMain: React.FC = () => {
  const finOpsContext = useFinTechContext();
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  // Chart Summary State
  const [summaries, setSummaries] = useState<Array<{ title: string; content: string; summaryId: string; timestamp: number }>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSplitPanelOpen, setIsSplitPanelOpen] = useState<boolean>(false);

  const addSummary = (title: string, content: string, summaryId = '') => {
    setSummaries((prev) => [{ title, content, summaryId, timestamp: Date.now() }, ...prev]);
  };

  const clearSummaries = () => {
    setSummaries([]);
  };

  const [dogmaGraphStatus, setDogmaGraphStatus] = useState<StatusType>('loading');
  const [dogmaGraphData, setDogmaGraphData] = useState<any>();

  const [dogmaDetailsTabStatus, setDogmaDetailsTabStatus] = useState<StatusType>('loading');
  const [dogmaDetailsTabData, setDogmaDetailsTabData] = useState<any>();

  const [summaryFilters, setSummaryFilters] = useState<string>('');
  const [originalFilterOptions, setOriginalFilterOptions] = useState<string>('');

  const displayNotification = (status: FlashbarProps.Type, message: string | React.ReactNode, linkMessage?: string) => {
    function addTicketLink() {
      if (linkMessage) {
        return (
          <Link variant="primary" target="_blank" href={linkMessage} color="inverted" fontSize="body-m">
            {' '}
            submit a ticket
          </Link>
        );
      }
    }
    setFlashbarItems([
      {
        type: status,
        content: (
          <>
            {message} 'or ' {addTicketLink()}
          </>
        ),
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setFlashbarItems([]),
        id: 'message_1'
      }
    ]);
  };

  return (
    <ChartSummaryContext.Provider
      value={{
        summaries,
        addSummary,
        isLoading,
        setIsLoading,
        isSplitPanelOpen,
        setIsSplitPanelOpen,
        clearSummaries
      }}
    >
      <AppLayout
        contentType="cards"
        headerSelector="#h"
        navigation={<DFPHomePageSideNavigation />}
        toolsHide={true}
        notifications={<Flashbar items={finOpsContext.flashbarItems} />}
        splitPanel={
          <SplitPanel
            header="Chart Summary"
            i18nStrings={{
              preferencesTitle: 'Split panel preferences',
              preferencesPositionLabel: 'Position',
              preferencesPositionDescription: 'Choose the default split panel position.',
              preferencesPositionSide: 'Side',
              preferencesPositionBottom: 'Bottom',
              preferencesConfirm: 'Confirm',
              preferencesCancel: 'Cancel',
              closeButtonAriaLabel: 'Close panel',
              openButtonAriaLabel: 'Open panel',
              resizeHandleAriaLabel: 'Resize split panel'
            }}
            closeBehavior="hide"
          >
            <ChartSummaryPanel />
          </SplitPanel>
        }
        onSplitPanelToggle={({ detail }) => setIsSplitPanelOpen(detail.open)}
        splitPanelPreferences={{ position: 'side' }}
        splitPanelOpen={isSplitPanelOpen}
        splitPanelSize={400}
        maxContentWidth={Number.MAX_VALUE}
        content={
          <>
            <DogmaContext.Provider
              value={{
                displayNotification,
                dogmaGraphStatus,
                setDogmaGraphStatus,
                dogmaGraphData,
                setDogmaGraphData,
                dogmaDetailsTabStatus,
                setDogmaDetailsTabStatus,
                dogmaDetailsTabData,
                setDogmaDetailsTabData,
                summaryFilters,
                setSummaryFilters,
                originalFilterOptions,
                setOriginalFilterOptions
              }}
            >
              <DogmaSelectionFilters />
              <Tabs
                tabs={[
                  {
                    label: 'Overview',
                    id: 'dogma-OV',
                    content: <DogmaOverviewPage />
                  },
                  {
                    label: 'Details',
                    id: 'dogma-DT',
                    content: <DogmaDetailsPage />
                  }
                ]}
              />
            </DogmaContext.Provider>
          </>
        }
      />
    </ChartSummaryContext.Provider>
  );
};
